import React from "react";
import {
  OBJECTIVE_TYPE,
  SUMMARY_TAG_LABEL,
  objectiveKey,
} from "../../_config/quarterlyOkrConfig";
import QokrObjectiveRow from "../../_shared/QokrObjectiveRow/QokrObjectiveRow";
import { StatePill } from "../../_shared/QokrPrimitives/QokrPrimitives";
import "./_summary-objectives.scss";

// ═══════════════════════════════════════════════════════════════════════════
// The quarter's objectives as they were left (§12): company objectives, then
// individual ones grouped by member. An untagged objective was locked; a tag
// says how far an unlocked one got. A coach mark, when there is one, sits
// next to the tag.
// ═══════════════════════════════════════════════════════════════════════════

// The summary names a company owner as a plain string, people elsewhere as
// `{ user_id, name }` — the row wants the object.
const asPerson = (person) =>
  typeof person === "string" ? { name: person } : person || null;

const groupByMember = (objectives) => {
  const groups = new Map();
  objectives.forEach((objective) => {
    const id = String(objective.member?.user_id);
    if (!groups.has(id)) {
      groups.set(id, { member: objective.member, objectives: [] });
    }
    groups.get(id).objectives.push(objective);
  });
  return [...groups.values()];
};

const Pills = ({ tag, mark }) => (
  <>
    {tag ? (
      <span className="qokr-pill qokr-pill--grey">
        {SUMMARY_TAG_LABEL[tag] || tag}
      </span>
    ) : null}
    {mark ? <StatePill state={mark.status} /> : null}
  </>
);

const SummaryObjectives = ({
  companyObjectives = [],
  individualObjectives = [],
  marks = {},
  activeKey = null,
}) => {
  const companyLabel = new Map(
    companyObjectives.map((objective, index) => [
      String(objective.objective_id),
      `O${index + 1} · ${objective.objective}`,
    ]),
  );

  const row = (type, objective, index, extra = {}) => {
    const key = objectiveKey(type, objective.objective_id);
    return (
      <QokrObjectiveRow
        key={key}
        index={index}
        statement={objective.objective}
        keyResults={objective.key_results || []}
        initiatives={objective.initiatives || []}
        pill={<Pills tag={objective.tag} mark={marks[key]} />}
        final={!objective.tag}
        isActive={activeKey === key}
        {...extra}
      />
    );
  };

  return (
    <div className="qokr-summary-okrs">
      <section className="qokr-summary-okrs__section">
        <h2 className="qokr-summary-okrs__title">Company objectives</h2>
        {companyObjectives.length ? (
          <ul className="cob__rows">
            {companyObjectives.map((objective, index) =>
              row(OBJECTIVE_TYPE.COMPANY, objective, index, {
                owner: asPerson(objective.owner),
              }),
            )}
          </ul>
        ) : (
          <p className="qokr-summary-okrs__empty">No company objectives.</p>
        )}
      </section>

      {individualObjectives.length ? (
        <section className="qokr-summary-okrs__section">
          <h2 className="qokr-summary-okrs__title">Individual objectives</h2>
          {groupByMember(individualObjectives).map((group) => (
            <div
              key={group.member?.user_id}
              className="qokr-summary-okrs__group"
            >
              <h3 className="qokr-summary-okrs__member">
                {group.member?.name}
              </h3>
              <ul className="cob__rows">
                {group.objectives.map((objective, index) =>
                  row(OBJECTIVE_TYPE.INDIVIDUAL, objective, index, {
                    link: {
                      kind: "individual",
                      label: companyLabel.get(
                        String(objective.company_objective_id),
                      ),
                    },
                  }),
                )}
              </ul>
            </div>
          ))}
        </section>
      ) : null}
    </div>
  );
};

export default SummaryObjectives;
